import React, { useEffect, useState, useMemo, useCallback } from "react";
import moment from "moment";
import { useFormik } from "formik";
import { enableRipple } from "@syncfusion/ej2-base";
import { DateRangePickerComponent } from "@syncfusion/ej2-react-calendars";
import { useDispatch, useSelector } from "react-redux";
import { setBreadCrumb } from "../../../middleware/actions/breadCrumbAction";
import { getProductVariant, isAdmin } from "../../../utilities";
import { fetchReportPublicationStatus } from "../../../middleware/services/reportApi";
import "./style.scss";
import { Dropdown } from "../../components/CustomFormElements/syncFusionFormFields";
import { hideLoader, showLoader } from "middleware/actions/utilityAction";
import TreeDataGridNew from "app/components/treeDataGridNew";
import { fetchConfig, fetchTaxonomyTrend } from "middleware/services/cmsApi";
import { ColumnDirective, ColumnsDirective } from "@syncfusion/ej2-react-grids";
import * as CONSTANTS from "../../../constants/index";
import { presets, transformContentTypeSubContentTypeData } from "utils";
import CheckBoxGroupDropDown from "app/components/CheckBoxGroupDropDown";
import CheckBoxGrouping from "../../components/CheckBoxGroupDropDown";
import { useQuery } from "@tanstack/react-query";

// specify breadcrumb here
const breadCrumbLinks = [{ linkUrl: "/report", linkName: "Report", linkActive: true }];
enableRipple(true);

// Publication Status Report component
const PublicationStatusReport = (isExpanded) => {
  const productVariant = getProductVariant();
  const [dateRange, setDateRange] = useState(null);
  const [isUpdated, setIsUpdated] = useState(undefined);
  const [startDate, setStartDate] = useState(moment(new Date()).format("dd-MMM-yyyy"));
  const [endDate, setEndDate] = useState(moment(new Date()).format("dd-MMM-yyyy"));
  const [rowData, setRowData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [gridRef, setGridRef] = useState(null);
  const [industryArray, setIndustryArray] = useState(["Food & Nutrition", "Energy", "Mobility", "Personal Care", "Industrial"]);

  const [allIndustries, setAllIndustries] = useState([]);
  const [selectedSubContentType, setSelectedSubContentType] = useState([]);
  const [selectedContentType, setSelectedContentType] = useState([]);
  const [selectedDeliveryTypes, setSelectedDeliveryTypes] = useState([]);

  let userCompetencyIndustry =
    localStorage.getItem("userCompetencyIndustry") !== "null" ? JSON.parse(localStorage.getItem("userCompetencyIndustry")) : [];

  const { data: deliveryTypeData } = useQuery({
    queryKey: ["delivery-type-config-publication-report"],
    queryFn: () => {
      let params = {};
      params.filters = [["configName", "eq", "delivery_type"]];
      return fetchConfig(params);
    },
    suspense: false,
    enabled: true,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: 1,
    refetchIntervalInBackground: false,
  });

  const deliveryTypeDataSource = useMemo(() => {
    if (selectedContentType?.length && deliveryTypeData?.data) {
      let commonDeliveryTypes = deliveryTypeData?.data?.data?.[0]?.configValue || [];

      let allDeliveryTypes = [];

      if (selectedContentType.includes(CONSTANTS.CONTENT_TYPE.CLIENT_BUSINESS_OBJECTIVE))
        allDeliveryTypes.push(CONSTANTS.CONTENT_TYPE.CLIENT_BUSINESS_OBJECTIVE);
      if (selectedContentType.includes(CONSTANTS.CONTENT_TYPE.TRAINING)) allDeliveryTypes.push(CONSTANTS.CONTENT_TYPE.TRAINING);
      if (
        selectedContentType.includes(CONSTANTS.CONTENT_TYPE.DASHBOARD) ||
        selectedContentType.includes(CONSTANTS.CONTENT_TYPE.DEVELOPMENTS_NEWS) ||
        selectedContentType.includes(CONSTANTS.CONTENT_TYPE.PROFILES_COMPANIES_TECHNOLOGIES) ||
        selectedContentType.includes(CONSTANTS.CONTENT_TYPE.SIGNALS) ||
        selectedContentType.includes(CONSTANTS.CONTENT_TYPE.WEBINARS_INTERVIEWS_PODCASTS) ||
        selectedContentType.includes(CONSTANTS.CONTENT_TYPE.INSIGHTS_REPORTS_RESEARCH)
      )
        allDeliveryTypes.push(...commonDeliveryTypes);

      let dataSource = allDeliveryTypes?.map((ele) => ({
        label: ele,
        value: ele,
      }));

      let validLabels = dataSource.map((item) => item.label);

      setSelectedDeliveryTypes(validLabels);

      return dataSource;
    } else return [];
  }, [deliveryTypeData?.data, selectedContentType]);

  const industrySource = useMemo(() => {
    if (isAdmin()) {
      const filterArray = industryArray.map((industry) => ({ label: industry.trendName, value: industry.trendName }));
      filterArray.unshift({ label: "All", value: "All" });
      return filterArray;
    }
    const uniqueIndustryNames = new Set();
    const industryList = userCompetencyIndustry
      .filter((industry) => {
        const industryName = industry.industryName;
        return !uniqueIndustryNames.has(industryName) && uniqueIndustryNames.add(industryName);
      })
      .map(({ industryName }) => ({ label: industryName, value: industryName }));
    industryList.unshift({ label: "All", value: "All" });
    return industryList;
  }, [userCompetencyIndustry, industryArray]);

  const ContentAndSubContentTypeGroupValues = useMemo(() => {
    const contentType = JSON.parse(localStorage.getItem("contentType")) || [];
    if (!contentType?.length) return [];
    const contentTypeDataSource =
      contentType
        ?.map((v) => ({ value: v.contentType, label: v.contentType, subContentType: v.subContentType }))
        .filter((item) => typeof item == "object")
        .sort((a, b) => a.label.localeCompare(b.label)) || [];

    const contentTypes = [];
    const subContentTypes = [];
    contentTypeDataSource.forEach((content) => {
      contentTypes.push(content.label);
      content?.subContentType.forEach((sub) => {
        subContentTypes.push(sub.contentType);
      });
    });
    setSelectedContentType([...contentTypes]);
    setSelectedSubContentType([...subContentTypes]);

    return transformContentTypeSubContentTypeData(contentTypeDataSource);
  }, []);

  const showLoaderGrid = () => dispatch(showLoader());
  const hideLoaderGrid = () => dispatch(hideLoader());

  const initialState = {
    reportIndustry: "All",
    reportContentType: [
      "Insights, Reports & Research",
      "Profiles - Companies & Technologies",
      "Dashboard",
      "Webinars, Interviews & Podcasts",
      "Developments & News",
      "Signals",
    ],
  };

  //** handle date range
  const fnStartDate = () => {
    let dateFrom = new Date();
    let dateTo = new Date();
    dateFrom.setDate(dateFrom.getDate() - CONSTANTS.ANALYTICS_REPORTS_DEFAULT_DATE_RANGE);
    const dateStart = dateFrom
      .toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
      .replace(/ /g, "-");
    dateTo.setDate(dateTo.getDate());
    const dateEnd = dateTo
      .toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
      .replace(/ /g, "-");
    setStartDate(new Date(dateStart));
    setEndDate(new Date(dateEnd));
    setDateRange([dateFrom, dateTo]);
    return [dateFrom, dateTo];
  };

  const gridState = useSelector((state) => state.gridState);
  const formik = useFormik({
    initialValues: {
      ...initialState,
    },
  });
  const dispatch = useDispatch();
  // specify column names for column fields in datGrid here
  const columnNames = {
    Industry: "industry",
    "Content Type": "contentType",
    Published: "published",
    Approved: "approved",
    "In Progress": "inProgress",
    Submitted: "submitted",
    Rejected: "rejected",
  };

  const recordsTemplate = (value) => {
    const a = value[`${value.column.field}`];
    return <span className={value.contentType === "" ? "span-link-weight" : ""}>{a}</span>;
  };

  const columnfields = [
    {
      field: "industry",
      headerText: "Industry",
      type: "String",
      textAlign: "Left",
      headerTextAlign: "Left",
      width: "140px",
      showInColumnChooser: false,
      allowFiltering: false,
      allowSorting: false,
      visible: true,
    },
    {
      field: "contentType",
      headerText: "Content Type",
      type: "String",
      textAlign: "Left",
      headerTextAlign: "Left",
      width: "140px",
      showInColumnChooser: false,
      allowFiltering: false,
      allowSorting: false,
      visible: true,
    },
    {
      field: "subContentType",
      headerText: "Sub Content Type",
      type: "String",
      textAlign: "Left",
      headerTextAlign: "Left",
      width: "140px",
      showInColumnChooser: false,
      allowFiltering: false,
      allowSorting: false,
      visible: true,
    },
    {
      field: "published",
      headerText: "Published",
      type: "Number",
      allowSorting: false,
      textAlign: "Center",
      headerTextAlign: "Center",
      width: "60px",
      visible: true,
      template: recordsTemplate,
    },
    {
      field: "approved",
      headerText: "Approved",
      type: "Number",
      allowSorting: false,
      textAlign: "Center",
      headerTextAlign: "Center",
      width: "60px",
      visible: true,
      template: recordsTemplate,
    },
    {
      field: "inProgress",
      headerText: "In Progress",
      type: "Number",
      allowSorting: false,
      textAlign: "Center",
      headerTextAlign: "Center",
      width: "60px",
      visible: true,
      template: recordsTemplate,
    },
    {
      field: "submitted",
      headerText: "Submitted",
      type: "Number",
      allowSorting: false,
      textAlign: "Center",
      headerTextAlign: "Center",
      width: "60px",
      visible: true,
      template: recordsTemplate,
    },
    {
      field: "rejected",
      headerText: "Rejected",
      type: "Number",
      allowSorting: false,
      textAlign: "Center",
      headerTextAlign: "Center",
      width: "60px",
      visible: true,
      template: recordsTemplate,
    },
  ];

  const fetchReportPublicationStatusReport = async (reportDateRange, params = {}) => {
    try {
      showLoaderGrid();
      let filters = [];
      params.productVariant = productVariant;
      let contentTypes = selectedContentType || [];
      let subContentTypes = selectedSubContentType || [];
      let deliveryType = selectedDeliveryTypes || [];

      filters.push(["contentType", "eq", contentTypes?.sort()]);

      filters.push([
        "industryName",
        "eq",
        formik.values.reportIndustry !== "All"
          ? [formik.values.reportIndustry]
          : industrySource.filter((item) => item.label !== "All").map((item) => item.label),
      ]);
      filters.push(["subContentType", "eq", subContentTypes?.sort()]);
      filters.push(["deliveryType", "eq", deliveryType?.sort()]);

      if (gridState?.recordsPerPage) params.limit = gridState.recordsPerPage;

      if (params.sort) {
        params.sort = params.sort.split(",").pop();
      }

      const DateFilter = dateRange === null ? reportDateRange : dateRange;

      if (DateFilter) {
        let gte = { d: DateFilter[0] };
        Date.prototype.toJSON = function () {
          return moment(this).format();
        };
        gte.d.toJSON = function () {
          return moment(this).format();
        };

        let lt = { d: moment(DateFilter[1]) };
        lt.d.toJSON = function () {
          return moment(this).format();
        };

        filters.push(["startDate", "gte", gte.d]);
        filters.push(["startDate", "lt", lt.d]);
      }
      params.filters = [...filters];

      const response = await fetchReportPublicationStatus({ ...params });
      setRowData(response?.data.data);
      setTotalCount(response?.data.totalCount);
      dispatch(hideLoader());
    } catch (error) {
      hideLoaderGrid();
      console.error("Error fetching data:", error);
    }
  };

  const onQueryCellInfo = (args) => {
    // console.log(args.data, "args")
    if (args.column.field === "Action") {
      args.colSpan = 3;
    }
  };
  const handleReset = () => {
    window.location.reload();
  };

  const handleSearch = () => {
    fetchReportPublicationStatusReport().then((res) => {
      if (res?.data) setIsUpdated(Date.now());
    });
  };

  const rowDataBound = (args) => {
    if (args.data.level == 0) {
      args.row.childNodes[0].style.fontWeight = "600";
    }
    (args.row?.childNodes[0]).style.fontSize = "14px";
    (args.row?.childNodes[0]).style.textAlign = "left";
  };

  const fetchIndustry = useCallback(async () => {
    try {
      showLoaderGrid();
      let params = {
        fields: ["id", "trendName"],
        filters: [["trendParentId", "eq", 0]],
        sort: "trendName:asc",
      };
      const result = await fetchTaxonomyTrend(params);
      // let productIndustryIds = result.data.data.map((item) => item.id);
      setIndustryArray(result.data.data);
      setAllIndustries(result.data.data.map((item) => item.trendName));
      return result.data.data;
    } catch (error) {
      hideLoaderGrid();
      console.error("Error fetching data:", error);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await fetchIndustry();
      hideLoaderGrid();
    })();
  }, []);

  useEffect(() => {
    if (allIndustries?.length == 0) return;
    (async () => {
      const reportDateRange = fnStartDate();
      await fetchReportPublicationStatusReport(reportDateRange);
      hideLoaderGrid();
    })();
  }, [allIndustries]);

  useEffect(() => {
    setIsUpdated(Date.now());
  }, [dateRange]);

  useEffect(() => {
    dispatch(setBreadCrumb(breadCrumbLinks));
    return () => {
      dispatch(setBreadCrumb());
    };
  }, [dispatch]);

  const getGridRef = (gridRefData) => {
    setGridRef(gridRefData);
  };

  const handleCreated = () => {
    showLoaderGrid();
    if (gridRef?.current) {
      gridRef.current.grid.columnChooserSettings.operator = "contains";
    }
    hideLoaderGrid();
  };

  return (
    <div className="px-3 grid-bg-white data-report-container">
      <div className="col-md-12 rounded-bottom nopadding p-0 bg-white">
        <div className="row">
          <div className="col-md-2 mt-3 grid-container-report-filters responsive-report-filters pr-0 publication-status-report-datepicker">
            <DateRangePickerComponent
              placeholder="Date Range"
              cssClass="e-outline analyst-query-datepicker"
              openOnFocus={true}
              showClearButton={false}
              format="dd MMM yyyy"
              separator="to"
              floatLabelType="Auto"
              // value={dateRange ? dateRange : ""}
              startDate={startDate}
              endDate={endDate}
              onChange={(e) => {
                if (e.value) setDateRange(e.value);
              }}
              autoComplete="off"
              max={new Date()}
              maxDays={CONSTANTS.PUBLICATION_STATUS_REPORT_DATE_RANGE_MAX}
              strictMode={true}
              presets={presets}
            ></DateRangePickerComponent>
          </div>
          <div className="col-md-2 mt-3">
            <Dropdown
              formik={formik}
              placeholder="Industry"
              required="required"
              id="reportIndustry"
              name="reportIndustry"
              fields={{ text: "label", value: "value" }}
              value={formik.values.reportIndustry}
              dataSource={industrySource}
            />
          </div>

          <div className={`col-md-2 checkBoxDropDownWrapper mt-3 mr-3`}>
            <CheckBoxGroupDropDown
              mode="CheckBox"
              placeholder="Content Types"
              filterBarPlaceholder="Search Content Types"
              checkFields={{ text: "label", value: "value", groupBy: "category" }}
              dataSource={ContentAndSubContentTypeGroupValues}
              enableGroupCheckBox={true}
              selectedValues={selectedSubContentType}
              setSelectedValues={setSelectedSubContentType}
              setSelectedCategories={setSelectedContentType}
              width="300px"
              customClass="ContentTypeCheckBoxGroupingDropDown"
            />
          </div>

          <div
            className={`checkBoxDropDownWrapper mt-3`}
            style={{
              marginLeft:
                window?.innerWidth > 1600
                  ? "20px"
                  : window?.innerWidth > 1440
                  ? "70px"
                  : window?.innerWidth > 1400
                  ? "90px"
                  : window?.innerWidth == 1366
                  ? "100px"
                  : window?.innerWidth > 1024
                  ? "110px"
                  : "0px",
            }}
          >
            <CheckBoxGrouping
              mode="CheckBox"
              checkFields={{ text: "label", value: "value" }}
              placeholder="Delivery Types"
              filterBarPlaceholder="Delivery Types"
              selectedValues={selectedDeliveryTypes}
              setSelectedValues={setSelectedDeliveryTypes}
              width="250px"
              customClass="ContentTypeCheckBoxGroupingDropDown"
              dataSource={deliveryTypeDataSource}
            />
          </div>

          {window?.innerWidth > 1600 ? (
            <div className="search-report col-md-2 mt-3 publication-status-report-search-btn">
              <button type="button" onClick={handleSearch} className="search-btn px-4 btn btn-primary mr-2">
                Search
              </button>
              <button type="reset" onClick={handleReset} className="btn btn-outline-dark px-4 ml-2 ">
                Reset
              </button>
            </div>
          ) : null}
        </div>
        {window?.innerWidth < 1600 && (
          <div className="row mr-2">
            <div className="col-12 d-flex justify-content-end mb-3 ">
              <button type="button" onClick={handleSearch} className="search-btn px-4 btn btn-primary mr-2">
                Search
              </button>
              <button type="reset" onClick={handleReset} className="btn btn-outline-dark px-4 ms-2">
                Reset
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="gennx-grid-client-detail-report analyst-query-report-grid col-md-10 ml-0 pl-0 publication-status-report">
        <TreeDataGridNew
          gridTitle="Client Activity Report"
          rowData={rowData}
          isPaging={false}
          showResetFilter={true}
          deleteMany={false}
          allowPagination={true}
          showExportButton={true}
          showLoader={showLoaderGrid}
          hideLoader={hideLoaderGrid}
          rowDataBound={rowDataBound}
          getGridRef={getGridRef}
          // created={handleCreated}
          childMapping="subTasks"
          showExpandCollapse={true}
        >
          <ColumnsDirective>
            {columnfields.map((column, index) => (
              <ColumnDirective key={index} {...column} />
            ))}
          </ColumnsDirective>
        </TreeDataGridNew>
      </div>
    </div>
  );
};

export default PublicationStatusReport;
